import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import api from '../services/api';
import { useToast } from '../hooks/useToast';

const التصنيفات = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addingCategory, setAddingCategory] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [newCategory, setNewCategory] = useState({ name: '', description: '', type: 'restaurant' });

  const { showToast } = useToast();

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      const data = await api.getCategories();
      // Backend returns { category: [...] }
      if (data && data.category && Array.isArray(data.category)) {
        // For better performance, fetch details only when needed (lazy loading)
        // For now, use basic info and fetch details on demand
        const categoriesBasic = data.category.map(cat => ({
          id: cat.id,
          name: cat.name,
          description: '', // Will be fetched on demand if needed
          type: cat.type,
          image_url: cat.imageUrl
        }));
        setCategories(categoriesBasic);
        
        // Fetch details in background (non-blocking)
        if (data.category.length > 0) {
          Promise.all(
            data.category.map(async (cat) => {
              try {
                const fullCategory = await api.getCategoryById(cat.id);
                return {
                  id: cat.id,
                  name: cat.name || fullCategory.name,
                  description: fullCategory.description || '',
                  type: cat.type || fullCategory.type,
                  image_url: cat.imageUrl || fullCategory.image_url
                };
              } catch (error) {
                return null;
              }
            })
          ).then(categoriesWithDetails => {
            // Update only if we got valid data
            const validCategories = categoriesWithDetails.filter(c => c !== null);
            if (validCategories.length > 0) {
              setCategories(validCategories);
            }
          }).catch(() => {
            // Silent fail - we already have basic info
          });
        }
      } else if (data && Array.isArray(data)) {
        // Fallback if response is direct array
        setCategories(data);
      } else {
        setCategories([]);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      showToast('حدث خطأ في جلب التصنيفات', 'error');
      setCategories([]);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const handleAdd = async () => {
    if (!newCategory.name || !newCategory.name.trim()) {
      showToast('يرجى إدخال اسم التصنيف', 'error');
      return;
    }

    if (!newCategory.description || !newCategory.description.trim()) {
      showToast('يرجى إدخال وصف التصنيف', 'error');
      return;
    }

    setAddingCategory(true);
    try {
      const result = await api.createCategory({
        name: newCategory.name.trim(),
        description: newCategory.description.trim(),
        type: newCategory.type,
      });
      
      // Add the new category to the list immediately (optimistic update)
      if (result && result.id) {
        const newCat = {
          id: result.id,
          name: result.name || newCategory.name.trim(),
          description: result.description || newCategory.description.trim(),
          type: result.type || newCategory.type,
          image_url: result.image_url || result.imageUrl
        };
        setCategories(prev => [newCat, ...prev]);
      }
      
      showToast('تم إضافة التصنيف بنجاح', 'success');
      setNewCategory({ name: '', description: '', type: 'restaurant' });
      setShowAddModal(false);
      
      // Refresh in background (non-blocking)
      fetchCategories(false);
    } catch (error) {
      console.error('Error creating category:', error);
      const errorMessage = error.message || error.data?.message || 'حدث خطأ في إضافة التصنيف';
      showToast(errorMessage, 'error');
    } finally {
      setAddingCategory(false);
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!editingCategory || !editingCategory.name.trim()) {
      showToast('يرجى إدخال اسم التصنيف', 'error');
      return;
    }

    try {
      await api.updateCategory(editingCategory.id, {
        name: editingCategory.name,
        description: editingCategory.description,
      });
      showToast('تم تحديث التصنيف بنجاح', 'success');
      setShowEditModal(false);
      setEditingCategory(null);
      fetchCategories();
    } catch (error) {
      console.error('Error updating category:', error);
      showToast('حدث خطأ في تحديث التصنيف', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا التصنيف؟')) {
      return;
    }

    try {
      await api.deleteCategory(id);
      showToast('تم حذف التصنيف بنجاح', 'success');
      fetchCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      showToast('حدث خطأ في حذف التصنيف', 'error');
    }
  };

  const getCategoryCount = (categoryId) => {
    // This would need to be calculated from restaurants/meals
    // For now, return a placeholder
    return 0;
  };

  return (
    <div style={{ 
      width: '100%', 
      height: '100vh', 
      display: 'flex', 
      flexDirection: 'column',
      backgroundColor: '#2B2F3F',
      overflow: 'hidden',
      direction: 'rtl'
    }}>
      <Header />
      <div style={{ 
        flex: 1, 
        padding: '32px', 
        backgroundColor: '#2B2F3F', 
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        boxSizing: 'border-box'
      }}>
        {/* Header Section */}
        <div style={{ 
          display: 'flex', 
          flexDirection: 'row',
          justifyContent: 'space-between', 
          alignItems: 'center',
          width: '100%'
        }}>
          <button
            onClick={() => setShowAddModal(true)}
            style={{
              padding: '12px 24px',
              borderRadius: '22px',
              border: 'none',
              backgroundColor: '#E96A3A',
              color: '#FFFFFF',
              fontFamily: 'El Messiri, sans-serif',
              fontSize: '12px',
              fontWeight: 500,
              cursor: 'pointer',
              height: '42px',
              boxSizing: 'border-box'
            }}
          >
            إضافة تصنيف
          </button>
          <h2 style={{ 
            fontFamily: 'El Messiri, sans-serif', 
            fontSize: '24px', 
            fontWeight: 700, 
            color: '#FFFFFF',
            margin: 0
          }}>
            التصنيفات
          </h2>
        </div>

        {/* Categories Grid */}
        {loading ? (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            padding: '40px',
            color: '#FFFFFF',
            fontFamily: 'Cairo, sans-serif'
          }}>
            جاري التحميل...
          </div>
        ) : categories.length === 0 ? (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            padding: '40px',
            color: '#FFFFFF',
            fontFamily: 'Cairo, sans-serif'
          }}>
            لا توجد تصنيفات
          </div>
        ) : (
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', 
            gap: '24px',
            width: '100%'
          }}>
            {categories.map((category) => (
              <div
                key={category.id}
                style={{
                  backgroundColor: '#FFFFFF',
                  borderRadius: '16px',
                  padding: '24px',
                  border: '1px solid rgba(233, 106, 58, 0.15)',
                  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.06)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px',
                  boxSizing: 'border-box',
                  transition: 'all 0.2s',
                  cursor: 'pointer',
                  minHeight: '180px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0px 8px 28px rgba(0, 0, 0, 0.12)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0px 4px 20px rgba(0, 0, 0, 0.06)';
                }}
              >
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'row',
                  justifyContent: 'space-between', 
                  alignItems: 'center'
                }}>
                  <h3 style={{ 
                    fontFamily: 'El Messiri, sans-serif', 
                    fontSize: '18px', 
                    fontWeight: 700, 
                    color: '#2B2F3F',
                    margin: 0
                  }}>
                    {category.name}
                  </h3>
                  <span style={{
                    padding: '6px 12px',
                    borderRadius: '20px',
                    backgroundColor: 'rgba(233, 106, 58, 0.1)',
                    color: '#E96A3A',
                    fontFamily: 'Cairo, sans-serif',
                    fontSize: '12px',
                    fontWeight: 600
                  }}>
                    {getCategoryCount(category.id)} عنصر
                  </span>
                </div>
                <p style={{ 
                  fontFamily: 'Cairo, sans-serif', 
                  fontSize: '14px', 
                  color: '#666A7A',
                  margin: 0,
                  textAlign: 'right',
                  lineHeight: '1.6',
                  flex: 1
                }}>
                  {category.description || 'لا يوجد وصف'}
                </p>
                <div style={{ 
                  display: 'flex', 
                  gap: '8px', 
                  justifyContent: 'flex-start',
                  marginTop: 'auto',
                  direction: 'rtl'
                }}>
                  <button 
                    onClick={() => handleEdit(category)}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '8px',
                      border: '1px solid rgba(102, 106, 122, 0.22)',
                      backgroundColor: 'transparent',
                      color: '#666A7A',
                      fontFamily: 'Cairo, sans-serif',
                      fontSize: '12px',
                      cursor: 'pointer',
                      fontWeight: 600
                    }}
                  >
                    تعديل
                  </button>
                  <button 
                    onClick={() => handleDelete(category.id)}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '8px',
                      border: 'none',
                      backgroundColor: '#F27D72',
                      color: '#FFFFFF',
                      fontFamily: 'Cairo, sans-serif',
                      fontSize: '12px',
                      cursor: 'pointer',
                      fontWeight: 600
                    }}
                  >
                    حذف
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add Modal */}
        {showAddModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(43, 47, 63, 0.63)',
            backdropFilter: 'blur(3px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            animation: 'fadeIn 0.2s ease-out'
          }}>
            <div style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '29px',
              padding: '48px',
              width: '499px',
              maxWidth: '90vw',
              border: '1px solid rgba(233, 106, 58, 0.12)',
              boxShadow: '0px 4px 23px 0px rgba(0, 0, 0, 0.04)',
              boxSizing: 'border-box',
              direction: 'rtl',
              animation: 'slideUp 0.3s ease-out'
            }}>
              <h3 style={{ 
                fontFamily: 'El Messiri, sans-serif', 
                fontSize: '20px', 
                fontWeight: 700, 
                color: '#2B2F3F',
                marginBottom: '24px',
                textAlign: 'right',
                margin: '0 0 24px 0'
              }}>
                إضافة تصنيف جديد
              </h3>
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: '16px' 
              }}>
                <input
                  type="text"
                  placeholder="اسم التصنيف"
                  value={newCategory.name}
                  onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                  disabled={addingCategory}
                  style={{
                    padding: '12px 16px',
                    borderRadius: '12px',
                    border: '1px solid rgba(102, 106, 122, 0.22)',
                    fontFamily: 'Cairo, sans-serif',
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box',
                    backgroundColor: addingCategory ? '#f9f9f9' : '#FFFFFF',
                    cursor: addingCategory ? 'not-allowed' : 'text',
                    opacity: addingCategory ? 0.7 : 1
                  }}
                  dir="rtl"
                />
                <textarea
                  placeholder="الوصف"
                  value={newCategory.description}
                  onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
                  disabled={addingCategory}
                  style={{
                    padding: '12px 16px',
                    borderRadius: '12px',
                    border: '1px solid rgba(102, 106, 122, 0.22)',
                    fontFamily: 'Cairo, sans-serif',
                    fontSize: '14px',
                    outline: 'none',
                    minHeight: '100px',
                    resize: 'vertical',
                    boxSizing: 'border-box',
                    backgroundColor: addingCategory ? '#f9f9f9' : '#FFFFFF',
                    cursor: addingCategory ? 'not-allowed' : 'text',
                    opacity: addingCategory ? 0.7 : 1
                  }}
                  dir="rtl"
                />
                <select
                  value={newCategory.type}
                  onChange={(e) => setNewCategory({ ...newCategory, type: e.target.value })}
                  disabled={addingCategory}
                  style={{
                    padding: '12px 16px',
                    borderRadius: '12px',
                    border: '1px solid rgba(102, 106, 122, 0.22)',
                    fontFamily: 'Cairo, sans-serif',
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box',
                    direction: 'rtl',
                    backgroundColor: addingCategory ? '#f9f9f9' : '#FFFFFF',
                    cursor: addingCategory ? 'not-allowed' : 'pointer',
                    opacity: addingCategory ? 0.7 : 1
                  }}
                >
                  <option value="restaurant">مطعم</option>
                  <option value="store">متجر</option>
                </select>
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'row',
                  gap: '12px', 
                  justifyContent: 'flex-end',
                  marginTop: '8px'
                }}>
                  <button
                    onClick={() => {
                      if (!addingCategory) {
                        setShowAddModal(false);
                        setNewCategory({ name: '', description: '', type: 'restaurant' });
                      }
                    }}
                    disabled={addingCategory}
                    style={{
                      padding: '12px 24px',
                      borderRadius: '12px',
                      border: '1px solid rgba(102, 106, 122, 0.22)',
                      backgroundColor: addingCategory ? '#f5f5f5' : 'transparent',
                      color: addingCategory ? '#999' : '#666A7A',
                      fontFamily: 'El Messiri, sans-serif',
                      fontSize: '12px',
                      cursor: addingCategory ? 'not-allowed' : 'pointer',
                      fontWeight: 500,
                      opacity: addingCategory ? 0.6 : 1,
                      transition: 'all 0.2s'
                    }}
                  >
                    إلغاء
                  </button>
                  <button
                    onClick={handleAdd}
                    disabled={addingCategory}
                    style={{
                      padding: '12px 24px',
                      borderRadius: '12px',
                      border: 'none',
                      backgroundColor: addingCategory ? '#d85a2a' : '#E96A3A',
                      color: '#FFFFFF',
                      fontFamily: 'El Messiri, sans-serif',
                      fontSize: '12px',
                      cursor: addingCategory ? 'not-allowed' : 'pointer',
                      fontWeight: 500,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      minWidth: '100px',
                      transition: 'all 0.2s',
                      position: 'relative'
                    }}
                  >
                    {addingCategory ? (
                      <>
                        <div style={{
                          width: '16px',
                          height: '16px',
                          border: '2px solid rgba(255, 255, 255, 0.3)',
                          borderTop: '2px solid #FFFFFF',
                          borderRadius: '50%',
                          animation: 'spin 0.6s linear infinite'
                        }}></div>
                        <span>جاري الإضافة...</span>
                      </>
                    ) : (
                      'إضافة'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* CSS for loading spinner and animations */}
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes fadeIn {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }
          @keyframes slideUp {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}</style>

        {/* Edit Modal */}
        {showEditModal && editingCategory && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(43, 47, 63, 0.63)',
            backdropFilter: 'blur(3px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '29px',
              padding: '48px',
              width: '499px',
              maxWidth: '90vw',
              border: '1px solid rgba(233, 106, 58, 0.12)',
              boxShadow: '0px 4px 23px 0px rgba(0, 0, 0, 0.04)',
              boxSizing: 'border-box',
              direction: 'rtl'
            }}>
              <h3 style={{ 
                fontFamily: 'El Messiri, sans-serif', 
                fontSize: '20px', 
                fontWeight: 700, 
                color: '#2B2F3F',
                marginBottom: '24px',
                textAlign: 'right',
                margin: '0 0 24px 0'
              }}>
                تعديل التصنيف
              </h3>
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: '16px' 
              }}>
                <input
                  type="text"
                  placeholder="اسم التصنيف"
                  value={editingCategory.name || ''}
                  onChange={(e) => setEditingCategory({ ...editingCategory, name: e.target.value })}
                  style={{
                    padding: '12px 16px',
                    borderRadius: '12px',
                    border: '1px solid rgba(102, 106, 122, 0.22)',
                    fontFamily: 'Cairo, sans-serif',
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                  dir="rtl"
                />
                <textarea
                  placeholder="الوصف"
                  value={editingCategory.description || ''}
                  onChange={(e) => setEditingCategory({ ...editingCategory, description: e.target.value })}
                  style={{
                    padding: '12px 16px',
                    borderRadius: '12px',
                    border: '1px solid rgba(102, 106, 122, 0.22)',
                    fontFamily: 'Cairo, sans-serif',
                    fontSize: '14px',
                    outline: 'none',
                    minHeight: '100px',
                    resize: 'vertical',
                    boxSizing: 'border-box'
                  }}
                  dir="rtl"
                />
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'row',
                  gap: '12px', 
                  justifyContent: 'flex-end',
                  marginTop: '8px'
                }}>
                  <button
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingCategory(null);
                    }}
                    style={{
                      padding: '12px 24px',
                      borderRadius: '12px',
                      border: '1px solid rgba(102, 106, 122, 0.22)',
                      backgroundColor: 'transparent',
                      color: '#666A7A',
                      fontFamily: 'El Messiri, sans-serif',
                      fontSize: '12px',
                      cursor: 'pointer',
                      fontWeight: 500
                    }}
                  >
                    إلغاء
                  </button>
                  <button
                    onClick={handleSaveEdit}
                    style={{
                      padding: '12px 24px',
                      borderRadius: '12px',
                      border: 'none',
                      backgroundColor: '#E96A3A',
                      color: '#FFFFFF',
                      fontFamily: 'El Messiri, sans-serif',
                      fontSize: '12px',
                      cursor: 'pointer',
                      fontWeight: 500
                    }}
                  >
                    حفظ
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default التصنيفات;
